import {
  UnavailabilityShopAddBeautyInput,
  UnavailabilityShopDeleteBeautyInput,
  UnavailabilityShopGetBeautyInput,
  UnavailabilityShopGetBeautyOutput,
  UnavailabilityShopTable,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { setOpenSnackbar } from "../store/snackbarReducers/snackbarSlice"
import { useAppDispatch } from "../store/store"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

export const useUnavailabilityShopQuery = () => {
  const { data, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ["unavailabilityShop"],
    queryFn: async () => {
      const userAttributes = await fetchUserAttributes()
      const token = await getToken()
      const queryParams = {
        shopId: userAttributes["custom:shopId"],
      } as UnavailabilityShopGetBeautyInput
      const dataReturn = await customAxios.get("/unavailability-shop", {
        params: queryParams,
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data as UnavailabilityShopGetBeautyOutput
    },
  })
  return {
    unavailabilityShop: data,
    isLoadingUnavailabilityShop: isLoading,
    errorUnavailabilityShop: isError,
    successUnavailabilityShop: isSuccess,
    refetchUnavailabilityShop: refetch,
  }
}

export const useAddUnavailabilityShopMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const mutation = useMutation({
    mutationFn: async (
      unavailabilityShop: UnavailabilityShopAddBeautyInput
    ) => {
      const token = await getToken()
      const userAttributes = await fetchUserAttributes()
      const shopId = userAttributes["custom:shopId"]

      // Optimistic update - ajouter immédiatement à la liste
      const tempId = `temp-${Date.now()}`
      const optimisticData: UnavailabilityShopTable = {
        id: tempId,
        shopId: shopId || "",
        startDate: unavailabilityShop.startDate,
        endDate: unavailabilityShop.endDate,
        processingDate: new Date().toISOString(),
        reason: unavailabilityShop.reason,
        triggerEmail: "false"
      }

      queryClient.setQueryData(
        ["unavailabilityShop"],
        (oldData: UnavailabilityShopTable[] | undefined) => {
          return [...(oldData || []), optimisticData]
        }
      )

      const dataReturn = await customAxios.post(
        "/unavailability-shop",
        unavailabilityShop,
        {
          headers: {
            Authorization: token,
          },
        }
      )
      return dataReturn.data
    },
    onSuccess: () => {
      // Invalider les queries pour récupérer les vraies données du serveur
      queryClient.invalidateQueries({ queryKey: ["unavailabilityShop"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Fermeture boutique ajoutée avec succès",
          severity: "success",
        })
      )
    },
    onError: () => {
      // En cas d'erreur, invalider pour revenir à l'état précédent
      queryClient.invalidateQueries({ queryKey: ["unavailabilityShop"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Erreur lors de l'ajout de la fermeture boutique",
          severity: "error",
        })
      )
    },
  })
  return mutation
}

export const useDeleteUnavailabilityShopMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const mutation = useMutation({
    mutationFn: async (unavailabilityShopId: string) => {
      const token = await getToken()
      const data: UnavailabilityShopDeleteBeautyInput = {
        id: unavailabilityShopId,
      }
      queryClient.setQueryData(
        ["unavailabilityShop"],
        (oldData: UnavailabilityShopTable[]) => {
          return oldData.filter(
            (unavailabilityShop) =>
              unavailabilityShop.id !== unavailabilityShopId
          )
        }
      )
      const dataReturn = await customAxios.delete("/unavailability-shop", {
        data,
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unavailabilityShop"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Indisponibilité supprimée",
          severity: "success",
        })
      )
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["unavailabilityShop"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Erreur lors de la suppression de l'indisponibilité",
          severity: "error",
        })
      )
    },
  })
  return mutation
}
